import { useEffect, useState } from 'react';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { GRADE_ORDINAL } from '../../utils/treeTypeGrades';
import type { GradeDiscount } from '../../types/nursery';

/**
 * What a lower grade costs: a % off the 1st grade's price, one figure per
 * grade for the whole organisation (Rob, 2026-10-12). Whatever price list or
 * tier gives the 1st grade its price, a 2nd is that less this %.
 */

interface Props {
  /** The most grades any tree type has; a grade below the 1st gets a row. */
  maxRank: number;
  discounts: GradeDiscount[];
  onSaved: (discounts: GradeDiscount[]) => void;
}

function GradeDiscountsCard({ maxRank, discounts, onSaved }: Props) {
  const [values, setValues] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const v: Record<number, string> = {};
    for (const d of discounts) v[d.grade_rank] = String(d.discount_pct);
    setValues(v);
  }, [discounts]);

  if (maxRank < 2) return null;
  const ranks = Array.from({ length: maxRank - 1 }, (_, i) => i + 2);
  const parsed = ranks.map((r) => ({ grade_rank: r, raw: values[r] ?? '' }));
  const bad = parsed.find((p) => p.raw !== '' && !(Number(p.raw) >= 0 && Number(p.raw) <= 100));

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body = parsed.filter((p) => p.raw !== '').map((p) => ({ grade_rank: p.grade_rank, discount_pct: Number(p.raw) }));
      const res = await nurseryService.setGradeDiscounts(body);
      if (!res.success) throw new Error(errorMessage(res, 'Could not save the grade prices'));
      onSaved(res.data);
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the grade prices'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Grade prices">
      <div>
        <h2 className="font-semibold text-neutral-900">Grade prices</h2>
        <p className="text-sm text-neutral-600 break-words">
          A lower grade sells at the 1st grade&apos;s price less this much, whatever price list or tier sets the 1st.
          Blank is the same price as the 1st.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {ranks.map((r) => (
          <label key={r} className="text-sm text-neutral-700 w-32">
            {GRADE_ORDINAL[r - 1]} grade
            <span className="mt-1 flex items-center gap-1">
              <input
                type="text" inputMode="decimal" value={values[r] ?? ''}
                aria-label={`${GRADE_ORDINAL[r - 1]} grade, % off the 1st`}
                onChange={(e) => { setSaved(false); setValues((v) => ({ ...v, [r]: e.target.value.replace(/[^0-9.]/g, '') })); }}
                className="w-20 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
              />
              <span className="text-neutral-500">% off</span>
            </span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={save} disabled={saving || !!bad}>{saving ? 'Saving…' : 'Save grade prices'}</Button>
        {bad && <span className="text-xs text-red-700">A % is between 0 and 100.</span>}
        {saved && <span className="text-xs text-green-700">Saved.</span>}
        {error && <span className="text-xs text-red-700 break-words min-w-0">{error}</span>}
      </div>
    </section>
  );
}

export default GradeDiscountsCard;
